import {useEffect, useRef, useState} from "react";
import {sized} from "@/lib/utils";
import {X, Phone, MapPin, Clock, Bookmark, ArrowUpRight, Star, Check, Image as ImageIcon, Loader2, ChevronLeft, ChevronRight, FileSearch} from "lucide-react";
import {apiGet, apiPost, apiUpload} from "@/lib/api";

function ImageSlider({images, name}) {
  const [i, setI] = useState(0);
  const n = images.length;
  return (
    <div className="image-slider" data-testid="business-image-slider">
      <img src={sized(images[i], 1000)} alt={`${name} ${i + 1}`} data-testid="slider-image" decoding="async"/>
      {n > 1 && (
        <>
          <button data-testid="slider-prev" className="slider-nav prev" onClick={() => setI((i - 1 + n) % n)}><ChevronLeft size={18}/></button>
          <button data-testid="slider-next" className="slider-nav next" onClick={() => setI((i + 1) % n)}><ChevronRight size={18}/></button>
          <div className="slider-dots">
            {images.map((_, k) => <button key={k} data-testid={`slider-dot-${k}`} className={k === i ? "active" : ""} onClick={() => setI(k)}/>)}
          </div>
          <span className="slider-count" data-testid="slider-count">{i + 1} / {n}</span>
        </>
      )}
    </div>
  );
}

export default function BusinessModal({business, close, notify, requireAuth, onReport, onGetFromIndia, asPage = false}) {
  const [reviews, setReviews] = useState([]);
  const [score, setScore] = useState({average: null, count: 0});
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    apiGet(`/listings/${business.listing_id}/reviews`).then(setReviews).catch(() => setReviews([]));
    apiGet(`/listings/${business.listing_id}/rating`).then(setScore).catch(() => {});
  }, [business.listing_id]);

  const handleFiles = async (files) => {
    const list = Array.from(files).slice(0, 4 - photos.length);
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of list) {
        if (f.size > 5 * 1024 * 1024) { notify(`${f.name} is larger than 5MB`); continue; }
        const r = await apiUpload(f);
        uploaded.push(r.url);
      }
      setPhotos(prev => [...prev, ...uploaded].slice(0, 4));
    } catch (e) {
      notify("Sign in to upload photos");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    requireAuth(async () => {
      try {
        const r = await apiPost(`/listings/${business.listing_id}/reviews`, {rating, text, photos});
        setReviews([r, ...reviews]);
        setText(""); setPhotos([]);
        apiGet(`/listings/${business.listing_id}/rating`).then(setScore).catch(() => {});
        notify("Your verified review was submitted");
      } catch { notify("Write at least a few words for your review"); }
    });
  };

  const body = (
    <>
        <ImageSlider images={business.images?.length ? business.images : [business.image]} name={business.name}/>
        <div className="business-modal-content">
          <div className="eyebrow">EVALUATED PRODUCT · {business.category}</div>
          <h2 data-testid="product-name">{business.name}</h2>
          {business.brand && business.brand !== business.name && <p className="product-brand" data-testid="product-brand">by <b>{business.brand}</b>{business.brand_details ? ` — ${business.brand_details}` : ""}</p>}
          {business.price && <div className="product-price-line" data-testid="product-price"><b>{business.price}</b>{business.mrp && <s>{business.mrp}</s>}{business.availability && <span>{business.availability}</span>}</div>}
          <div className="rating-line" data-testid="product-rating"><strong>{"★".repeat(Math.round(score.average || 0)) || "☆☆☆☆☆"} {score.average ?? "New"}</strong><span>{score.count} member reviews</span><span>· {business.location}</span></div>
          <p>{business.description} Our team has evaluated this product and keeps this page current.</p>
          {business.highlights?.length > 0 && <ul className="product-highlights" data-testid="product-highlights">{business.highlights.map(h => <li key={h}><Check size={13}/> {h}</li>)}</ul>}
          <div className="business-contact">
            {business.location && <span><MapPin size={16}/> {business.location}</span>}
            {business.variants && <span><Bookmark size={16}/> {business.variants}</span>}
            {business.shipping && <span><Clock size={16}/> {business.shipping}</span>}
            {business.returns && <span><Phone size={16}/> {business.returns}</span>}
          </div>
          {business.details && (
            <dl className="product-details" data-testid="business-details">
              {Object.entries(business.details).map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}
            </dl>
          )}
          <div className="business-modal-actions">
            <button data-testid="business-modal-pdr" className="outline-button" onClick={onReport}><FileSearch size={16}/> Purchase Decision Report</button>
            <button data-testid="business-modal-quote" className="coral-button" onClick={onGetFromIndia}>
              Ask my concierge <ArrowUpRight size={16}/>
            </button>
          </div>
          <div className="review-section">
            <div className="eyebrow">CUSTOMER REVIEWS ({reviews.length})</div>
            {reviews.map(r => (
              <div className="review-item" key={r.review_id}>
                <strong>{"★".repeat(r.rating)} <span>{r.author}</span></strong>
                <small><Check size={12}/> Verified customer</small>
                <p>{r.text}</p>
                {r.photos?.length > 0 && (
                  <div className="review-photos">
                    {r.photos.map((p, i) => <img key={i} src={p} alt={`Review ${i+1}`} loading="lazy"/>)}
                  </div>
                )}
              </div>
            ))}
            <form className="review-form" onSubmit={submit}>
              <strong>Share your experience</strong>
              <div className="review-stars">
                {[1,2,3,4,5].map(n => (
                  <button type="button" data-testid={`review-star-${n}`} className={n <= rating ? "selected-star" : ""} onClick={() => setRating(n)} key={n}>
                    <Star size={16} fill="currentColor"/>
                  </button>
                ))}
              </div>
              <textarea data-testid="review-text" required value={text} onChange={e => setText(e.target.value)} placeholder="What should other members know?"/>
              <div className="review-photo-row">
                <input ref={fileRef} data-testid="review-photos-input" type="file" accept="image/*" multiple style={{display:"none"}} onChange={e => handleFiles(e.target.files)}/>
                <button type="button" data-testid="review-add-photos" className="outline-button sm" onClick={() => fileRef.current?.click()} disabled={photos.length >= 4 || uploading}>
                  {uploading ? <Loader2 size={14} className="spin"/> : <ImageIcon size={14}/>}
                  Add photos ({photos.length}/4)
                </button>
                <div className="review-thumbs">
                  {photos.map((p, i) => (
                    <div key={i} className="review-thumb">
                      <img src={p} alt="preview"/>
                      <button type="button" onClick={() => setPhotos(photos.filter((_, j) => j !== i))}><X size={11}/></button>
                    </div>
                  ))}
                </div>
              </div>
              <button data-testid="review-submit" className="coral-button" type="submit">Submit verified review</button>
            </form>
          </div>
        </div>
    </>
  );
  if (asPage) return <div className="product-page" data-testid="product-page">{body}</div>;
  return (
    <div className="modal-backdrop" onClick={close}>
      <div className="business-modal" onClick={e => e.stopPropagation()}>
        <button data-testid="business-modal-close" className="modal-close" onClick={close}><X size={18}/></button>
        {body}
      </div>
    </div>
  );
}
